/**
 * Real-Time Alert Service (Frontend)
 *
 * SSE client for receiving real-time alerts from the backend.
 * Features:
 * - Auto-reconnect on disconnect
 * - Heartbeat monitoring
 * - Event subscription pattern
 */

import { logger } from '../utils/logger';

// Alert types matching backend
export enum AlertType {
  SHELL_GAME_DETECTED = 'SHELL_GAME_DETECTED',
  CRITICAL_VIOLATION = 'CRITICAL_VIOLATION',
  FATAL_CRASH = 'FATAL_CRASH',
  OOS_ORDER_ISSUED = 'OOS_ORDER_ISSUED',
  BASIC_THRESHOLD_CROSSED = 'BASIC_THRESHOLD_CROSSED',
  CONTACT_MATCH_FOUND = 'CONTACT_MATCH_FOUND',
  RISK_SCORE_INCREASED = 'RISK_SCORE_INCREASED',
  NEW_CARRIER_IN_NETWORK = 'NEW_CARRIER_IN_NETWORK',
  INSURANCE_EXPIRING = 'INSURANCE_EXPIRING',
  AUTHORITY_REVOKED = 'AUTHORITY_REVOKED',
}

export enum AlertSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export interface AlertData {
  [key: string]: unknown;
  dotNumber?: number;
  carrierName?: string;
  violationCode?: string;
  crashType?: string;
  basicCategory?: string;
  riskScore?: number;
  threshold?: number;
  currentValue?: number;
}

export interface Alert {
  id: string;
  userId: number;
  dotNumber?: number;
  alertType: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  data: AlertData;
  createdAt: string;
  readAt?: string;
  acknowledgedAt?: string;
  dismissedAt?: string;
}

export interface AlertPreference {
  alertType: AlertType;
  enabled: boolean;
  emailNotify: boolean;
  smsNotify: boolean;
  inAppNotify: boolean;
  browserNotify: boolean;
  minSeverity: AlertSeverity;
}

export interface WatchlistEntry {
  id: number;
  dotNumber: number;
  legalName: string;
  dbaName?: string;
  entityType?: string;
  location?: string;
  safetyRating?: string;
  authStatus?: string;
  notes?: string;
  addedAt: string;
  alertSettings: {
    violations: boolean;
    crashes: boolean;
    scoreChanges: boolean;
    networkChanges: boolean;
    shellGame: boolean;
    contactMatch: boolean;
    oos: boolean;
    fatal: boolean;
  };
  alertCount: number;
  unreadCount: number;
}

type AlertListener = (alert: Alert) => void;
type ConnectionListener = (connected: boolean) => void;
type SSEMessage = {
  type: 'connected' | 'initial' | 'new' | 'updated' | 'dismissed' | 'all_read' | 'error';
  connectionId?: string;
  alerts?: Alert[];
  alert?: Alert;
  alertId?: string;
  updates?: Partial<Alert>;
  message?: string;
};

const RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * Real-Time Alert Service Class
 */
export class RealtimeAlertService {
  private eventSource: EventSource | null = null;
  private alertListeners: Set<AlertListener> = new Set();
  private connectionListeners: Set<ConnectionListener> = new Set();
  private reconnectAttempts: number = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isConnecting: boolean = false;
  private connectionId: string | null = null;

  /**
   * Connect to SSE stream
   */
  connect(): void {
    if (this.eventSource || this.isConnecting) {
      logger.debug('AlertService: Already connected or connecting');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      logger.warn('AlertService: No auth token found, cannot connect');
      return;
    }

    this.isConnecting = true;

    try {
      // Build URL with auth token in query param since EventSource doesn't support headers
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const token = localStorage.getItem('token');

      // Add token to query params for SSE authentication
      const url = token
        ? `${baseUrl}/api/realtime-alerts/stream?token=${encodeURIComponent(token)}`
        : `${baseUrl}/api/realtime-alerts/stream`;

      logger.debug('AlertService: Connecting to SSE', { hasToken: !!token });

      // Create EventSource with credentials
      this.eventSource = new EventSource(url, {
        withCredentials: true,
      });

      this.eventSource.onopen = () => {
        logger.info('AlertService: SSE connection opened');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.notifyConnectionListeners(true);
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data: SSEMessage = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          logger.error('AlertService: Error parsing SSE message', error);
        }
      };

      this.eventSource.onerror = (error) => {
        // Only log at warn level - errors are expected during reconnection
        if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS - 1) {
          logger.error('AlertService: SSE connection failed after max retries');
        } else {
          logger.debug('AlertService: SSE connection error, will retry', { attempt: this.reconnectAttempts + 1 });
        }
        this.handleDisconnect();
      };
    } catch (error) {
      logger.error('AlertService: Error creating EventSource', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from SSE stream
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.isConnecting = false;
    this.connectionId = null;
    this.notifyConnectionListeners(false);
    logger.info('AlertService: SSE connection closed');
  }

  /**
   * Subscribe to new alerts
   */
  subscribeToAlerts(callback: AlertListener): () => void {
    this.alertListeners.add(callback);
    return () => this.alertListeners.delete(callback);
  }

  /**
   * Subscribe to connection status changes
   */
  subscribeToConnection(callback: ConnectionListener): () => void {
    this.connectionListeners.add(callback);
    return () => this.connectionListeners.delete(callback);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Get connection ID
   */
  getConnectionId(): string | null {
    return this.connectionId;
  }

  // Private methods

  private handleMessage(data: SSEMessage): void {
    switch (data.type) {
      case 'connected':
        this.connectionId = data.connectionId || null;
        logger.info('AlertService: Connected', { connectionId: this.connectionId });
        break;

      case 'initial':
        // Initial batch of unread alerts
        if (data.alerts) {
          logger.info('AlertService: Received initial alerts', { count: data.alerts.length });
          data.alerts.forEach(alert => this.notifyAlertListeners(alert));
        }
        break;

      case 'new':
        // New alert
        if (data.alert) {
          logger.info('AlertService: New alert', { title: data.alert.title });
          this.notifyAlertListeners(data.alert);
        }
        break;

      case 'updated':
        // Alert was updated (read, acknowledged)
        logger.debug('AlertService: Alert updated', { alertId: data.alertId });
        break;

      case 'dismissed':
        // Alert was dismissed
        logger.debug('AlertService: Alert dismissed', { alertId: data.alertId });
        break;

      case 'all_read':
        // All alerts marked as read
        logger.debug('AlertService: All alerts marked as read');
        break;

      case 'error':
        logger.error('AlertService: Server error', { message: data.message });
        break;

      default:
        logger.warn('AlertService: Unknown message type', { type: data.type });
    }
  }

  private handleDisconnect(): void {
    this.isConnecting = false;

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.connectionId = null;
    this.notifyConnectionListeners(false);
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      return; // Already scheduled
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.error('AlertService: Max reconnect attempts reached');
      return;
    }

    const delay = RECONNECT_DELAY_MS * Math.pow(1.5, this.reconnectAttempts);
    logger.info('AlertService: Scheduling reconnect', { delay, attempt: this.reconnectAttempts + 1 });

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private notifyAlertListeners(alert: Alert): void {
    this.alertListeners.forEach(listener => {
      try {
        listener(alert);
      } catch (error) {
        logger.error('AlertService: Error in alert listener', error);
      }
    });
  }

  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(listener => {
      try {
        listener(connected);
      } catch (error) {
        logger.error('AlertService: Error in connection listener', error);
      }
    });
  }
}

// Singleton instance
export const realtimeAlertService = new RealtimeAlertService();

// API functions for REST endpoints
import api from './api';

export const realtimeAlertApi = {
  // Get paginated alerts
  getAlerts: async (options: {
    limit?: number;
    offset?: number;
    types?: AlertType[];
    severities?: AlertSeverity[];
    unreadOnly?: boolean;
    dotNumber?: number;
  } = {}): Promise<{ alerts: Alert[]; pagination: { limit: number; offset: number; total: number; hasMore: boolean } }> => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.types) params.append('types', options.types.join(','));
    if (options.severities) params.append('severities', options.severities.join(','));
    if (options.unreadOnly) params.append('unreadOnly', 'true');
    if (options.dotNumber) params.append('dotNumber', options.dotNumber.toString());

    const { data } = await api.get(`/realtime-alerts?${params.toString()}`);
    return data;
  },

  // Get unread alerts
  getUnreadAlerts: async (limit?: number): Promise<{ alerts: Alert[]; unreadCount: number }> => {
    const params = limit ? `?limit=${limit}` : '';
    const { data } = await api.get(`/realtime-alerts/unread${params}`);
    return data;
  },

  // Get alert statistics
  getAlertStats: async (): Promise<{
    total: number;
    unread: number;
    bySeverity: { critical: number; high: number; warning: number; info: number };
    byType: { shellGame: number; contactMatch: number; fatalCrash: number };
  }> => {
    const { data } = await api.get('/realtime-alerts/stats');
    return data;
  },

  // Mark alert as read
  markAsRead: async (alertId: string): Promise<void> => {
    await api.post(`/realtime-alerts/${alertId}/read`);
  },

  // Mark all alerts as read
  markAllAsRead: async (): Promise<{ markedCount: number }> => {
    const { data } = await api.post('/realtime-alerts/read-all');
    return data;
  },

  // Acknowledge alert
  acknowledgeAlert: async (alertId: string): Promise<void> => {
    await api.post(`/realtime-alerts/${alertId}/acknowledge`);
  },

  // Dismiss alert
  dismissAlert: async (alertId: string): Promise<void> => {
    await api.delete(`/realtime-alerts/${alertId}`);
  },

  // Get alert preferences
  getPreferences: async (): Promise<AlertPreference[]> => {
    const { data } = await api.get('/realtime-alerts/preferences');
    return data;
  },

  // Update alert preferences
  updatePreferences: async (alertType: AlertType, updates: Partial<AlertPreference>): Promise<void> => {
    await api.put(`/realtime-alerts/preferences/${alertType}`, updates);
  },

  // Create test alert (dev only)
  createTestAlert: async (options?: {
    alertType?: AlertType;
    severity?: AlertSeverity;
    dotNumber?: number;
  }): Promise<Alert | null> => {
    const { data } = await api.post('/realtime-alerts/test', options || {});
    return data;
  },
};

// Watchlist API
export const watchlistApi = {
  // Get user's watchlist
  getWatchlist: async (): Promise<{ watchlist: WatchlistEntry[]; total: number }> => {
    const { data } = await api.get('/monitoring/carriers');
    return { watchlist: data.data || [], total: data.count || 0 };
  },

  // Check if carrier is watched
  checkWatchlist: async (dotNumber: number): Promise<{ isWatched: boolean; addedAt: string | null }> => {
    const { data } = await api.get(`/monitoring/carriers/${dotNumber}`);
    return data;
  },

  // Add carrier to watchlist
  addToWatchlist: async (
    dotNumber: number,
    options?: { notes?: string; alertSettings?: WatchlistEntry['alertSettings'] }
  ): Promise<{ id: number; dotNumber: number; legalName: string; addedAt: string }> => {
    const { data } = await api.post(`/monitoring/carriers/${dotNumber}`, options || {});
    return data;
  },

  // Update watchlist settings
  updateWatchlistSettings: async (
    dotNumber: number,
    updates: { notes?: string; alertSettings?: Partial<WatchlistEntry['alertSettings']> }
  ): Promise<void> => {
    await api.put(`/monitoring/carriers/${dotNumber}`, updates);
  },

  // Remove from watchlist
  removeFromWatchlist: async (dotNumber: number): Promise<void> => {
    await api.delete(`/monitoring/carriers/${dotNumber}`);
  },

  // Get alerts for watched carrier
  getWatchlistAlerts: async (
    dotNumber: number,
    options?: { limit?: number; offset?: number }
  ): Promise<{ alerts: Alert[]; pagination: { limit: number; offset: number; total: number } }> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const { data } = await api.get(`/monitoring/alerts?dotNumber=${dotNumber}&${params.toString()}`);
    return data;
  },
};
